/**
 * Publicador — interruptor por cliente del publicador del CM (webhooks de
 * ClickUp → Zernio) y lo que fue decidiendo.
 *
 *   Apagado → publica Mission Control.
 *   Dry-run → el CM registra qué haría, sin publicar.
 *   Activo  → publica el CM. Exige el slug en PUBLISHER_ACTIVE_ALLOWLIST (deploy)
 *             y confirmar escribiendo el slug. MC tiene que estar apagado antes.
 *
 * Solo rol admin (el backend lo exige en cada endpoint). Ver docs/PUBLICADOR.md.
 */
import { useState } from "react";
import { Lock, RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn";
import { relativeTime } from "@/lib/format";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  PUBLISHER_MODES,
  usePublisher,
  usePublisherRuns,
  useRunSweep,
  useSetPublisherList,
  useSetPublisherMode,
  type PublisherClient,
  type PublisherMode,
  type PublisherRun,
} from "@/hooks/use-publisher";

const ghostBtn =
  "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-xs font-medium transition-colors disabled:opacity-50";

const DECISION_LABEL: Record<string, string> = {
  would_publish: "Publicaría",
  would_escalate: "Escalaría",
  published: "Programado",
  escalated: "Escalado",
  skipped: "En espera",
  error: "Error",
};

export function PublicadorPage() {
  const { data: user, isLoading } = useCurrentUser();
  const [cliente, setCliente] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="font-sans text-sm text-text-muted">Cargando…</div>
      </div>
    );
  }
  if (user && user.role !== "admin") {
    return (
      <div className="min-h-full bg-background overflow-y-auto">
        <div className="max-w-2xl mx-auto px-7 py-16 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-surface-2 border border-default items-center justify-center mb-4">
            <Lock size={26} strokeWidth={1.5} className="text-text-muted" />
          </div>
          <h1 className="font-sans text-2xl font-semibold text-text-primary">Acceso restringido</h1>
          <p className="font-sans text-sm text-text-muted mt-2">Esta sección es solo para administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-7 py-8 space-y-6">
        <header>
          <h1 className="font-sans text-3xl font-semibold tracking-tight text-text-primary">Publicador</h1>
          <p className="font-sans text-sm text-text-muted mt-1">
            El CM publica en redes a partir de los webhooks de ClickUp. Se pasa cliente por cliente:
            primero dry-run al lado de Mission Control, después activo con MC apagado para ese cliente.
          </p>
        </header>
        <Clientes seleccionado={cliente} onSeleccionar={setCliente} />
        <Corridas cliente={cliente} />
      </div>
    </div>
  );
}

function Clientes({ seleccionado, onSeleccionar }: { seleccionado: string | null; onSeleccionar: (s: string | null) => void }) {
  const { data, isLoading } = usePublisher();
  const sweep = useRunSweep();

  return (
    <section aria-labelledby="pub-clientes" className="card-surface p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 id="pub-clientes" className="font-sans text-md font-semibold text-text-primary">Clientes</h2>
        <button type="button" className={ghostBtn} onClick={() => sweep.mutate()} disabled={sweep.isPending}>
          <RefreshCw size={12} className={cn(sweep.isPending && "animate-spin")} />
          Barrer ahora
        </button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-2 font-sans text-[11px]">
          <Estado ok={data.config.webhookConfigured} label="Secreto del webhook" />
          <Estado ok={data.config.clickupConfigured} label="ClickUp" />
          <Estado ok={data.config.zernioConfigured} label="Zernio" />
          <span className="text-text-muted">
            Barrido de respaldo: {data.config.sweepMinutes > 0 ? `cada ${data.config.sweepMinutes} min` : "apagado"} ·
            webhook: <code className="font-mono">{data.config.webhookUrl}</code>
          </span>
        </div>
      )}

      {isLoading && <div className="font-sans text-sm text-text-muted">Cargando…</div>}
      <ul className="divide-y divide-default">
        {data?.clients.map((c) => (
          <ClienteFila
            key={c.clientSlug}
            cliente={c}
            seleccionado={seleccionado === c.clientSlug}
            onSeleccionar={() => onSeleccionar(seleccionado === c.clientSlug ? null : c.clientSlug)}
          />
        ))}
      </ul>
    </section>
  );
}

function Estado({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center h-6 px-2 rounded-md border",
        ok ? "border-default text-text-secondary" : "border-magenta text-magenta",
      )}
    >
      {label}: {ok ? "ok" : "falta"}
    </span>
  );
}

function ClienteFila({ cliente, seleccionado, onSeleccionar }: { cliente: PublisherClient; seleccionado: boolean; onSeleccionar: () => void }) {
  const setMode = useSetPublisherMode();
  const setList = useSetPublisherList();
  const sinLista = cliente.clickupLists.length === 0;

  const vincular = () => {
    const pegado = window.prompt(
      `Lista de ClickUp de ${cliente.clientSlug} (el cronopost donde se marca "Listo para programar").\n\n` +
        `Pegá el link de la lista (…/v/li/901…) o su ID. Vacío = desvincular.`,
      cliente.clickupLists[0] ?? "",
    );
    if (pegado === null) return;
    setList.mutate({ clientSlug: cliente.clientSlug, list: pegado.trim() || null });
  };

  const cambiar = (mode: PublisherMode) => {
    if (mode === cliente.mode) return;
    if (mode === "active") {
      const escrito = window.prompt(
        `Activar el publicador del CM para ${cliente.clientSlug}.\n\n` +
          `Antes: apagar el publicador de Mission Control para este cliente (todas sus redes). ` +
          `Si no, se publica dos veces.\n\nEscribí el slug para confirmar:`,
      );
      if (escrito === null) return;
      setMode.mutate({ clientSlug: cliente.clientSlug, mode, confirmSlug: escrito.trim() });
      return;
    }
    setMode.mutate({ clientSlug: cliente.clientSlug, mode });
  };

  return (
    <li className="py-3 flex items-center gap-3 flex-wrap">
      <button type="button" onClick={onSeleccionar} className="min-w-0 flex-1 text-left">
        <div className={cn("font-sans text-sm font-medium", seleccionado ? "text-magenta" : "text-text-primary")}>
          {cliente.clientSlug}
        </div>
        <div className="font-sans text-[11px] text-text-muted">
          {cliente.platforms.join(", ")}
          {sinLista ? " · sin lista de ClickUp" : ` · lista ${cliente.clickupLists.join(", ")}`}
          {cliente.updatedBy && ` · ${cliente.updatedBy} ${relativeTime(cliente.updatedAt!)}`}
        </div>
      </button>
      <button type="button" className={ghostBtn} onClick={vincular} disabled={setList.isPending}>
        {sinLista ? "Vincular lista" : "Cambiar lista"}
      </button>
      <div role="radiogroup" aria-label={`Publicador de ${cliente.clientSlug}`} className="inline-flex rounded-lg border border-default overflow-hidden">
        {PUBLISHER_MODES.map((m) => {
          const deshabilitado =
            setMode.isPending || (m.value !== "off" && sinLista) || (m.value === "active" && !cliente.activeAllowed);
          return (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={cliente.mode === m.value}
              title={m.value === "active" && !cliente.activeAllowed ? "Falta el slug en PUBLISHER_ACTIVE_ALLOWLIST (deploy)" : m.description}
              disabled={deshabilitado}
              onClick={() => cambiar(m.value)}
              className={cn(
                "h-8 px-3 font-sans text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                cliente.mode === m.value
                  ? m.value === "active"
                    ? "bg-magenta text-white"
                    : "bg-surface-2 text-text-primary"
                  : "text-text-secondary hover:bg-surface-2",
              )}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </li>
  );
}

function Corridas({ cliente }: { cliente: string | null }) {
  const { data: runs, isLoading } = usePublisherRuns(cliente);
  return (
    <section aria-labelledby="pub-corridas" className="card-surface p-5 space-y-3">
      <h2 id="pub-corridas" className="font-sans text-md font-semibold text-text-primary">
        Qué decidió {cliente ? <span className="font-mono">{cliente}</span> : "el publicador"}
      </h2>
      {isLoading && <div className="font-sans text-sm text-text-muted">Cargando…</div>}
      {runs && runs.length === 0 && (
        <p className="font-sans text-sm text-text-muted">Todavía no evaluó ninguna tarea.</p>
      )}
      <ul className="divide-y divide-default">
        {runs?.map((r) => <CorridaFila key={r.id} run={r} />)}
      </ul>
    </section>
  );
}

function CorridaFila({ run }: { run: PublisherRun }) {
  const [abierta, setAbierta] = useState(false);
  const plan = run.plan as { plataformas?: Array<{ platform: string }>; fecha?: string | null; publishNow?: boolean; material?: { source?: string } };
  return (
    <li className="py-2.5">
      <button type="button" onClick={() => setAbierta(!abierta)} className="w-full text-left" aria-expanded={abierta}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className={cn(
              "font-sans text-[11px] font-medium px-1.5 py-0.5 rounded",
              run.mode === "dry_run" ? "bg-surface-2 text-text-secondary" : "bg-magenta text-white",
            )}
          >
            {run.mode === "dry_run" ? "dry-run" : "activo"} · {DECISION_LABEL[run.decision] ?? run.decision}
          </span>
          <a
            href={`https://app.clickup.com/t/${run.clickupTaskId}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-text-primary hover:text-magenta"
          >
            {run.customTaskId ?? run.clickupTaskId}
          </a>
          <span className="font-sans text-xs text-text-secondary truncate min-w-0">{run.taskName}</span>
          <span className="font-sans text-[11px] text-text-muted ml-auto">{relativeTime(run.createdAt)}</span>
        </div>
        <div className="font-sans text-xs text-text-muted mt-0.5">
          {run.reason ??
            [
              plan.plataformas?.map((p) => p.platform).join(" + "),
              plan.publishNow ? "ahora" : plan.fecha ? new Date(plan.fecha).toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" }) : null,
              plan.material?.source,
            ]
              .filter(Boolean)
              .join(" · ")}
        </div>
      </button>
      {abierta && (
        <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-surface-1 border border-default p-2 font-mono text-[11px] text-text-secondary whitespace-pre-wrap break-all">
          {JSON.stringify(run.plan, null, 2)}
        </pre>
      )}
    </li>
  );
}
